import { useEffect, useState } from "react";
import { CheckCircle, XCircle, Info, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * ProvisioningChecklist — V1.1.1 (Batch B #5).
 *
 * "Is this user ready to sign in?" Complements the Permission Audit
 * Suite which answers "what can this user access?".
 *
 * Renders the 7-row checklist returned by
 * GET /api/admin/users/{id}/provisioning-checklist:
 *   1. Active status
 *   2. Microsoft Entra account
 *   3. Role assigned
 *   4. Dashboard widgets granted
 *   5. Applications assigned
 *   6. Notifications configured   (informational-only)
 *   7. Ready for first login      (derived from the above)
 *
 * Read-only view — no fields are edited from this component. Admins
 * still change status/role/apps through the existing Users panel and
 * Application Registry.
 */
export default function ProvisioningChecklist({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/users/${userId}/provisioning-checklist`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load checklist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]);

  if (!userId) return null;

  return (
    <div
      data-testid="provisioning-checklist"
      className="rounded-md border border-slate-200 bg-white"
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">Provisioning Checklist</div>
          <div className="text-[11px] text-slate-500">
            Confirms this user is fully ready to sign in for the first time.
          </div>
        </div>
        <button
          data-testid="provisioning-checklist-refresh"
          onClick={load}
          disabled={loading}
          className="rounded-md border border-slate-300 bg-white p-1 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          aria-label="Reload checklist"
        >
          <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : undefined} />
        </button>
      </div>

      {loading && (
        <div className="py-6 text-center text-xs text-slate-500">Loading…</div>
      )}
      {error && (
        <div className="p-3 text-sm text-amber-800">{error}</div>
      )}

      {data && !loading && (
        <>
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span
                data-testid="provisioning-summary-badge"
                data-ready={data.summary.ready ? "true" : "false"}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold ${
                  data.summary.ready
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : "border-amber-300 bg-amber-50 text-amber-700"
                }`}
              >
                {data.summary.ready ? (
                  <>
                    <CheckCircle size={11} weight="fill" /> Ready for first login
                  </>
                ) : (
                  <>
                    <XCircle size={11} weight="fill" /> Not ready ({data.summary.blocking_failures} failing)
                  </>
                )}
              </span>
              <span className="text-slate-500">
                {data.summary.ok} of {data.summary.total} checks passing
              </span>
            </div>
          </div>

          <ol data-testid="provisioning-checklist-items" className="divide-y divide-slate-100">
            {data.items.map((item) => (
              <ChecklistRow key={item.key} item={item} />
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function ChecklistRow({ item }) {
  const info = !!item.informational;
  return (
    <li
      data-testid={`provisioning-item-${item.key}`}
      data-ok={item.ok ? "true" : "false"}
      data-informational={info ? "true" : "false"}
      className="flex items-start gap-3 px-3 py-2"
    >
      <div className="flex-shrink-0 pt-0.5">
        {item.ok ? (
          <CheckCircle size={14} weight="fill" className="text-emerald-600" />
        ) : info ? (
          <Info size={14} weight="fill" className="text-slate-400" />
        ) : (
          <XCircle size={14} weight="fill" className="text-amber-600" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900">{item.label}</div>
        <div className="mt-0.5 text-xs text-slate-600">{item.detail}</div>
      </div>
      {item.count && (
        <div className="text-[11px] font-mono text-slate-500">
          {item.count.granted} / {item.count.total}
        </div>
      )}
    </li>
  );
}
